import { Logger } from 'besonders-logger'
import stringify from 'safe-stable-stringify'
import { finalizeApplogForInsert } from '../applog/applog-helpers.ts'
import { dateNowIso, matchPartStatic } from '../applog/applog-utils.ts'
import { Applog, ApplogForInsert, ApplogValue, CidString, DatalogQueryPattern, ValueOrMatcher } from '../applog/datom-types.ts'
import { createDebugName } from '../utils/debug-name.ts'
import { memoizedFn } from '../query/memoized.ts'
import { SubscribableImpl } from '../query/subscribable.ts'
import type { Subscribable } from '../query/subscribable.ts'
import { isInitEvent, Thread, ThreadEvent } from './basic.ts'
import { type DeltaContext, type DeltaEvent, MappedThread, type ThreadDerivation } from './mapped.ts'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO) // eslint-disable-line no-unused-vars

export const rollingFilter = memoizedFn('rollingFilter', function rollingFilter(
	thread: Thread,
	pattern: DatalogQueryPattern,
	opts: { name?: string; extraFilterName?: string } = {},
) {
	const filter = makeFilter(pattern)

	const derivation: ThreadDerivation = {
		compute: (parents) => {
			if (parents.length !== 1) {
				throw ERROR(`rollingFilter requires exactly one parent`, { parents: parents.length })
			}
			return filter(parents[0].applogs)
		},
		mapDelta: (delta, { state }) => {
			const mappedDelta: DeltaEvent = {
				added: filter(delta.added),
				removed: delta.removed?.filter(log => state.includes(log)),
			}
			VERBOSE(
				`rollingFilter{${thread.nameAndSizeUntracked} | ${opts.name ? ` '${opts.name}'}` : ''} parentUpdate`,
				pattern,
				delta,
				'=>',
				mappedDelta,
			)
			return mappedDelta
		},
	}
	return new MappedThread(
		`${thread.name} | ${opts.name || `rollingFilter{${stringify(pattern)}}`}`,
		thread,
		[...thread.filters, ...(opts.extraFilterName ? [opts.extraFilterName] : [])],
		derivation,
	)
}, {
	argsDebugName: (thread, pattern, opts) => createDebugName({ caller: 'rollingFilter', thread, pattern, args: opts }),
})

export const rollingMapper = memoizedFn('rollingMapper', function rollingMapper(
	thread: Thread,
	derivation: ThreadDerivation,
	opts: { name?: string; extraFilterName?: string } = {},
) {
	return new MappedThread(
		`${thread.name} | ${opts.name || `rollingMapper`}`,
		thread,
		[...thread.filters, ...(opts.extraFilterName ? [opts.extraFilterName] as const : [] as const)],
		derivation,
	)
}, {
	argsDebugName: (thread, _derivation, opts) => createDebugName({ caller: 'rollingMapper', thread, args: opts }),
})

export const rollingAcc = memoizedFn(
	'rollingAcc',
	function rollingAcc<ACC>(
		thread: Thread,
		acc: ACC,
		eventMapper: (event: ThreadEvent, acc: ACC) => void,
		opts: { name?: string } = {},
	): Subscribable<ACC> {
		eventMapper({ init: thread.applogs }, acc) // Do initial mapping

		const result = new SubscribableImpl<ACC>(acc, () =>
			thread.subscribe(event => {
				eventMapper(event, acc)
				result._set(acc) // notify — same reference but contents mutated
			}, 'derived'),
			{ equals: false }, // accumulator is mutated in-place
		)

		return result
	},
	{ argsDebugName: (thread, _acc, _mapper, opts) => `rollingAcc{${thread.nameAndSizeUntracked}${opts?.name ? ` | ${opts?.name}` : ''}}` },
)

export const getUntrackedPattern = function getUntrackedPattern(
	pattern: DatalogQueryPattern,
) {
	if (!Object.entries(pattern).length) {
		throw new Error(`Pattern is empty`)
	}
	return pattern
}
export function makeFilter(
	pattern: DatalogQueryPattern,
) {
	return function madeFilter(logs: readonly Applog[]) {
		return logs.filter(function madeFilterSingleLog(applog) {
			for (const field of Object.keys(pattern)) {
				let patternValue = pattern[field] // (i) not using .entries bc. https://gists.cwidanage.com/2018/06/how-to-iterate-over-object-entries-in.html
				if (patternValue === undefined) continue // undefined in pattern means "no constraint/query on this field"
				const applogValue = applog[field.startsWith('!') ? field.slice(1) : field]
				const patternValT: ValueOrMatcher<ApplogValue> = patternValue
				if (!matchPartStatic(field as keyof Applog, patternValT, applogValue)) {
					return false
				}
			}
			return true
		})
	}
}

/**
 * // ! think twice before using
 */
export const getUntrackedFilterResults = function getUntrackedFilterResults(
	thread: Thread,
	pattern: DatalogQueryPattern,
	opts: { name?: string } = {},
) {
	const untrackedPattern = getUntrackedPattern(pattern)
	const filter = makeFilter(untrackedPattern)
	return filter(thread.applogs)
}

///////////////////////////
// FILTERED THREAD TYPES //
///////////////////////////
export type ThreadWithFilter<T extends string> = Thread & {
	filters: readonly T[]
}
export function hasFilter<T extends string>(thread: Thread, filter: T): thread is ThreadWithFilter<T> {
	return thread.filters.includes(filter)
}
export function assertRaw(thread: Thread) /* : thread is ThreadWithoutFilters */ {
	if (thread.filters.length) {
		WARN(`[assertRaw] but '${thread.nameAndSizeUntracked}' has filters:`, thread.filters)
	}
	return thread as ThreadWithoutFilters
}
export function assertOnlyCurrent(thread: Thread) /* : thread is ThreadOnlyCurrent */ {
	if (
		!hasFilter(thread, 'lastWriteWins') ||
		!hasFilter(thread, 'withoutDeleted')
	) throw ERROR(`should be filtered thread, but is:`, thread.filters)
	return thread
}
export type ThreadOnlyCurrent = ThreadWithFilter<'lastWriteWins'>
export type ThreadNoDeleted = ThreadWithFilter<'withoutDeleted'>
export type ThreadOnlyCurrentNoDeleted = ThreadWithFilter<'lastWriteWins' | 'withoutDeleted'>
export type ThreadWithoutFilters = Thread & { filters: [] }

/** Re-export for convenience */
export const asReadOnly = MappedThread.asReadOnly

//////////////////
// MISC HELPERS //
//////////////////

export type ApplogMapper = (log: Applog, sourceThread: Thread) => ApplogForInsert
export const simpleApplogMapper = function simpleApplogMapper(
	thread: Thread,
	logMapper: ApplogMapper,
	opts: { name?: string; extraFilterName?: string } = {},
) {
	const mappedTo = new Map<CidString, Applog>() // source log CID to result log
	const mapLogs = (applogs: readonly Applog[], thread: Thread) => {
		const ts = dateNowIso()
		return applogs.map(log => {
			const mapped = logMapper(log, thread)
			let mapTo: Applog
			if (mapped === log) {
				mapTo = log
			} else {
				if ((mapped as Applog).cid === log.cid) {
					delete (mapped as Applog).cid // for convenience of not needing to remove it in the mapper
				}
				mapTo = finalizeApplogForInsert(mapped, {
					ts,
					threadForPv: null, // ? should not be inferred, right?
				})
			}
			mappedTo.set(log.cid, mapTo)
			return mapTo
		})
	}

	const derivation: ThreadDerivation = {
		compute(parents) {
			if (parents.length !== 1) {
				throw ERROR(`simpleApplogMapper requires exactly one parent`, { parents: parents.length })
			}
			const [parent] = parents
			mappedTo.clear()
			return mapLogs(parent.applogs, parent)
		},
		mapDelta: (delta, { state }) => {
			const mappedDelta: DeltaEvent = {
				added: mapLogs(delta.added, thread),
				removed: delta.removed?.map(removedSourceLog => {
					const mappedLog = mappedTo.get(removedSourceLog.cid)
					if (!mappedLog) {
						throw ERROR(`[simpleApplogMapper] Parent remove event for Applog that we don't know about`, { removedSourceLog })
					}
					mappedTo.delete(removedSourceLog.cid)
					return mappedLog
				}).filter(log => state.includes(log)),
			}
			VERBOSE(
				`simpleApplogMapper{${thread.nameAndSizeUntracked} | ${opts?.name ? ` '${opts?.name}'}` : ''} parentUpdate`,
				delta,
				'=>',
				mappedDelta,
			)
			return mappedDelta
		},
	}
	const mappedThread = rollingMapper(thread, derivation, opts)
	VERBOSE.isDisabled || VERBOSE(`simpleApplogMapper<${thread.nameAndSizeUntracked}> initial mapped to`, mappedThread.applogs)
	return mappedThread
}
